import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { initGoogleSignIn } from '../utils/googleAuth';
import { toast } from 'react-toastify';
import { setDoctorSession, shouldSkipAutoLogin, clearAuthBounce } from '../utils/auth';
import TwoFactorOtpStep from '../components/TwoFactorOtpStep';

function DoctorLogin() {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState('password');
  const [awaiting2fa, setAwaiting2fa] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', otp: '' });
  const [loading, setLoading] = useState(false);
  const initOnceRef = useRef(false);

  const getApiError = (err) => {
    const data = err.response?.data;
    if (!data) return err.message || 'Login failed';
    if (typeof data === 'string') return data;
    return (
      data.message ||
      data.error ||
      data.msg ||
      data.detail ||
      'Login failed. Please check your credentials.'
    );
  };

  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;

    const init = async () => {
      try {
        await api.get('doctor/login/');
      } catch {
        /* ignore */
      }

      try {
        const res = await api.get('doctor/session-check/');
        if (!res.data?.authenticated) return;

        if (shouldSkipAutoLogin()) {
          try {
            await api.post('doctor/logout/');
          } catch {
            /* ignore */
          }
          clearAuthBounce();
          return;
        }

        setDoctorSession(res.data.doctor_id);
        navigate('/doctor/dashboard', { replace: true });
      } catch {
        /* not logged in */
      }
    };

    init();
  }, [navigate]);

  useEffect(() => {
    const handleGoogleResponse = async (response) => {
      try {
        setLoading(true);
        const res = await api.post('doctor/google-login/', { token: response.credential });
        if (res.data.doctor_id || res.data.message) {
          setDoctorSession(res.data.doctor_id);
          navigate('/doctor/dashboard', { replace: true });
        } else {
          toast.error('Google login failed. Please try again.', {
            position: 'bottom-right',
            autoClose: 3000,
          });
        }
      } catch (err) {
        toast.error(getApiError(err), {
          position: 'bottom-right',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      initGoogleSignIn(handleGoogleResponse);
    }, 100);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const sendOtp = async () => {
    try {
      if (!formData.email) {
        toast.warning('Please enter email first', {
          position: 'bottom-right',
          autoClose: 3000,
        });
        return;
      }
      await api.post('doctor/send-email-otp/', { email: formData.email });
      toast.success('OTP sent successfully to your email', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch {
      toast.error('Failed to send OTP', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    }
  };

  const resend2faOtp = async () => {
    try {
      await api.post('doctor/resend-2fa-otp/');
      toast.success('OTP resent to your email', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(getApiError(err), {
        position: 'bottom-right',
        autoClose: 3000,
      });
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const email = formData.email.trim().toLowerCase();

      if (awaiting2fa) {
        const response = await api.post('doctor/verify-2fa/', {
          email,
          otp: formData.otp.trim(),
        });
        if (response.data.doctor_id || response.data.message) {
          setAwaiting2fa(false);
          setDoctorSession(response.data.doctor_id);
          toast.success(response.data.message || 'Login successful!', {
            position: 'bottom-right',
            autoClose: 2000,
          });
          navigate('/doctor/dashboard', { replace: true });
        } else {
          toast.error('Login failed. Please try again.', {
            position: 'bottom-right',
            autoClose: 3000,
          });
        }
        return;
      }

      const url = loginType === 'password' ? 'doctor/login/' : 'doctor/verify-email-otp/';
      const payload =
        loginType === 'password'
          ? { email, password: formData.password }
          : { email, otp: formData.otp };

      const response = await api.post(url, payload);

      if (loginType === 'password' && response.data?.requires_2fa) {
        setAwaiting2fa(true);
        setFormData((prev) => ({ ...prev, otp: '' }));
        toast.success(response.data.message || 'OTP sent to your email', {
          position: 'bottom-right',
          autoClose: 3000,
        });
        return;
      }

      if (response.data.doctor_id || response.data.message) {
        setDoctorSession(response.data.doctor_id);
        navigate('/doctor/dashboard', { replace: true });
      } else {
        toast.error('Login failed. Please try again.', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast.error(getApiError(err), {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light vh-100 d-flex align-items-center overflow-hidden position-relative py-3">
      <button
        type="button"
        className="btn btn-link btn-sm text-muted text-decoration-none position-absolute top-0 start-0 m-3"
        onClick={() => navigate(-1)}
      >
        <i className="bi bi-arrow-left me-1"></i> Back to Home
      </button>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 col-xl-5">
            <div className="text-center mb-3">
              <div className="bg-primary d-inline-flex rounded-circle shadow-sm p-2 mb-2">
                <i
                  className={`bi ${awaiting2fa ? 'bi-shield-lock-fill' : 'bi-person-badge-fill'} fs-4 text-white`}
                ></i>
              </div>
              <h2 className="fw-bold text-primary fs-4 mb-1">FITNOVA</h2>
              <p className="text-muted mb-0 small">
                {awaiting2fa ? 'Two-factor authentication' : 'Doctor Portal: Welcome back!'}
              </p>
            </div>

            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-4 px-md-5 py-md-4">
                {!awaiting2fa && (
                  <div className="nav nav-pills nav-fill bg-light p-1 rounded-pill mb-3">
                    <button
                      type="button"
                      className={`nav-link rounded-pill py-2 ${loginType === 'password' ? 'active shadow-sm' : ''}`}
                      onClick={() => setLoginType('password')}
                    >
                      Password
                    </button>
                    <button
                      type="button"
                      className={`nav-link rounded-pill py-2 ${loginType === 'otp' ? 'active shadow-sm' : ''}`}
                      onClick={() => setLoginType('otp')}
                    >
                      OTP Login
                    </button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {awaiting2fa ? (
                    <TwoFactorOtpStep
                      email={formData.email}
                      otp={formData.otp}
                      onOtpChange={(value) => setFormData((prev) => ({ ...prev, otp: value }))}
                      onResend={resend2faOtp}
                      onBack={() => {
                        setAwaiting2fa(false);
                        setFormData((prev) => ({ ...prev, otp: '', password: '' }));
                      }}
                      loading={loading}
                    />
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="form-label small fw-bold">Professional Email</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0 text-muted">
                            <i className="bi bi-envelope"></i>
                          </span>
                          <input
                            type="email"
                            name="email"
                            placeholder="doctor@fitnova.com"
                            className="form-control border-start-0 bg-light"
                            onChange={handleChange}
                            required
                            value={formData.email}
                          />
                        </div>
                      </div>

                      {loginType === 'password' ? (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between">
                            <label className="form-label small fw-bold">Password</label>
                            <a
                              href="#"
                              className="small text-decoration-none"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate('/doctor/forgot-password');
                              }}
                            >
                              Forgot?
                            </a>
                          </div>
                          <div className="input-group">
                            <span className="input-group-text bg-light border-end-0 text-muted">
                              <i className="bi bi-shield-lock"></i>
                            </span>
                            <input
                              type="password"
                              name="password"
                              placeholder="••••••••"
                              className="form-control border-start-0 bg-light"
                              onChange={handleChange}
                              required
                              value={formData.password}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <label className="form-label small fw-bold">One-Time Password</label>
                          <div className="input-group mb-2">
                            <span className="input-group-text bg-light border-end-0 text-muted">
                              <i className="bi bi-key"></i>
                            </span>
                            <input
                              type="text"
                              name="otp"
                              placeholder="Enter 6-digit OTP"
                              className="form-control border-start-0 bg-light"
                              onChange={handleChange}
                              value={formData.otp}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={sendOtp}
                          >
                            Send code to email
                          </button>
                        </div>
                      )}

                      <button
                        className="btn btn-primary w-100 py-2 fw-bold rounded-3 shadow-sm mb-2 d-flex align-items-center justify-content-center"
                        disabled={loading}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                          <i className="bi bi-box-arrow-in-right me-2"></i>
                        )}
                        Login
                      </button>
                    </>
                  )}
                </form>

                {!awaiting2fa && (
                  <>
                    <div className="position-relative text-center my-3">
                      <hr className="text-muted" />
                      <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small">
                        OR
                      </span>
                    </div>

                    <div className="mb-3">
                      <div id="googleSignInDiv"></div>
                    </div>

                    <div className="text-center">
                      <p className="text-muted small mb-0">
                        Not registered yet?
                        <button
                          className="btn btn-link btn-sm fw-bold text-decoration-none"
                          onClick={() => navigate('/doctor/register')}
                        >
                          Join as Doctor
                        </button>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorLogin;
